import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  AfterViewChecked,
  inject,
  effect,
  computed,
  Input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';

import { ChatInputComponent } from '../chat-input/chat-input.component';
import {
  AI_CHAT_CONFIG,
  FileAttachment,
  formatThreadDate,
} from '../ai-chat.config';
import { AuthStore } from '@rapider/authentication';
import { ProjectDataService } from '@rapider/project-data';
import { DataSourceActions, selectDataSources } from '@rapider/data-source';

import { AiChatActions } from '../state/ai-chat.actions';
import {
  selectMessages,
  selectIsAiThinking,
  selectThreads,
  selectActiveThreadId,
  selectHasMoreThreads,
  selectIsLoadingThreads,
  selectIsLoadingMessages,
} from '../state/ai-chat.reducer';
import { ChatMessageBubbleComponent } from '../chat-message-bubble/chat-message-bubble.component';
import { ChatV2MessageBubbleComponent } from '../v2';

/**
 * PURE FUNCTION: Safely retrieves the localStorage override for the Chat V2 feature toggle.
 * Handles browser environment checks and error safety to prevent SSR/testing crashes.
 *
 * @returns {string | null} Stored string ('true' | 'false') or null if unset / non-browser.
 */
export function getChatV2StorageOverride(): string | null {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      return window.localStorage.getItem('RAPIDER_CHAT_V2');
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * PURE FUNCTION: Resolves the effective initial state for the Chat V2 feature toggle.
 * Prioritizes explicit local storage override, then configuration constants, then default.
 *
 * @param {string | null | undefined} storageOverride - Value from localStorage ('true' | 'false').
 * @param {boolean} [configDefault=true] - Workspace default from AI_CHAT_CONFIG.constants.useV2.
 * @returns {boolean} True if Chat V2 should be active, false otherwise.
 */
export function resolveInitialChatV2State(
  storageOverride?: string | null,
  configDefault = true,
): boolean {
  if (storageOverride === 'true') {
    return true;
  }
  if (storageOverride === 'false') {
    return false;
  }
  return configDefault;
}

/**
 * Smart Component responsible for orchestrating the active chat view and its associated threads sidebar.
 * Connects directly to the NgRx store to read messages, thread lists, and loading states.
 */
@Component({
  selector: 'rapider-ai-chat',
  standalone: true,
  imports: [
    CommonModule,
    ChatInputComponent,
    ChatMessageBubbleComponent,
    ChatV2MessageBubbleComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.scss',
})
export class AiChatComponent implements OnInit, AfterViewChecked {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  @Input() additionalData?: Record<string, any>;
  @Input() aiFunctionIdOverride?: string;

  /**
   * Feature toggle input allowing consumers to explicitly enable or disable Chat V2.
   * Accepts boolean or string attribute representation (e.g. useV2="true" / useV2="false").
   */
  @Input() set useV2(value: boolean | string | undefined) {
    if (value !== undefined) {
      const normalized =
        typeof value === 'string'
          ? value === 'true' || value === ''
          : Boolean(value);
      this._useV2Signal.set(normalized);
    }
  }

  /** Internal reactive signal storing the current Chat V2 activation state */
  private readonly _useV2Signal = signal<boolean>(
    resolveInitialChatV2State(
      getChatV2StorageOverride(),
      (AI_CHAT_CONFIG.constants as { useV2?: boolean }).useV2 ?? true,
    ),
  );

  /** Public computed signal exposing Chat V2 active state to the template */
  public readonly isV2 = computed(() => this._useV2Signal());

  private readonly store = inject(Store);
  private readonly authStore = inject(AuthStore);
  private readonly projectDataService = inject(ProjectDataService);
  public readonly config = AI_CHAT_CONFIG;
  public formatThreadDate = formatThreadDate;

  private dataSources = this.store.selectSignal(selectDataSources);
  private projectApplications = signal<any[]>([]);

  /** Computed signals with safe fallback types to prevent undefined template errors during lazy-loading */
  private messagesSignal = this.store.selectSignal(selectMessages);
  public messages = computed(() => this.messagesSignal() || []);
  private isAiThinkingSignal = this.store.selectSignal(selectIsAiThinking);
  public isAiThinking = computed(() => this.isAiThinkingSignal() || false);

  /** Computed list of messages strictly visible in the UI (filters out internal/hidden messages) */
  public visibleMessages = computed(() =>
    this.messages().filter((m) => m.isShownInChat !== false && !m.isHidden),
  );

  /** Whether any visible AI message is currently streaming, so the separate bouncing dots can be suppressed */
  public hasStreamingAiMessage = computed(() => {
    const list = this.visibleMessages();
    if (list.length === 0) return false;
    const lastMsg = list[list.length - 1];
    return (
      !lastMsg.isUser &&
      (Boolean(lastMsg.isStreaming) ||
        Boolean(
          lastMsg.reasoningSteps &&
            lastMsg.reasoningSteps.length > 0 &&
            this.isAiThinking(),
        ))
    );
  });

  private threadsSignal = this.store.selectSignal(selectThreads);
  public threads = computed(() => this.threadsSignal() || []);
  private activeThreadIdSignal = this.store.selectSignal(selectActiveThreadId);
  public activeThreadId = computed(() => this.activeThreadIdSignal() || null);
  private hasMoreThreadsSignal = this.store.selectSignal(selectHasMoreThreads);
  public hasMoreThreads = computed(() => this.hasMoreThreadsSignal() || false);
  private isLoadingThreadsSignal = this.store.selectSignal(
    selectIsLoadingThreads,
  );
  public isLoadingThreads = computed(
    () => this.isLoadingThreadsSignal() || false,
  );
  private isLoadingMessagesSignal = this.store.selectSignal(
    selectIsLoadingMessages,
  );
  public isLoadingMessages = computed(
    () => this.isLoadingMessagesSignal() || false,
  );

  /** Tracks if a scroll calculation is pending on the next view lifecycle check */
  private shouldScrollToBottom = false;

  constructor() {
    // Whenever the messages array receives new data, trigger a scroll to bottom
    effect(() => {
      const msgs = this.messages();
      if (msgs && msgs.length > 0) {
        this.shouldScrollToBottom = true;
      }
    });

    // Reactive project sync: when activeProject changes, re-init chat, load data sources and applications
    effect(() => {
      const project = this.authStore.activeProject();
      const projectId = project?.id;
      if (projectId) {
        this.store.dispatch(AiChatActions.initChat());
        this.store.dispatch(DataSourceActions.loadDataSources());
        this.projectDataService
          .getProjectApplications(projectId)
          .subscribe((apps) => {
            this.projectApplications.set(apps || []);
          });
      } else {
        this.projectApplications.set([]);
      }
    });
  }

  /**
   * Triggers the initialization effect flow (fetching channels, threads, and latest history)
   * as soon as the component loads.
   */
  ngOnInit(): void {
    this.store.dispatch(AiChatActions.loadAvailableAiFunctions());
    if (this.aiFunctionIdOverride) {
      this.store.dispatch(
        AiChatActions.selectAiFunction({
          aiFunctionId: this.aiFunctionIdOverride,
        }),
      );
    }
  }

  /** Handle for the light debounce timer used by typewriter typing progress */
  private _immediateScrollDebounceTimer: ReturnType<typeof setTimeout> | null =
    null;

  /** Tracks whether the user is actively scrolling up reading history */
  private _isUserScrolling = false;

  /** Handle for active requestAnimationFrame scroll transition */
  private _scrollAnimationId: number | null = null;

  /**
   * Lifecycle hook executed after Angular finishes DOM updates.
   * Flushes any pending auto-scrolls to the bottom of the chat.
   */
  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.performAutoScroll(false);
      this.shouldScrollToBottom = false;
    }
  }

  /**
   * Tracks user scroll actions to prevent auto-scrolling when the user is reading history.
   */
  public onContainerScroll(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this._isUserScrolling = distanceFromBottom > 120;
  }

  /**
   * Smoothly animates the chat scroll container to the bottom.
   * Matches Flutter mobile _performAutoScroll with 150ms easeOutCubic curve and light debounce.
   *
   * @param immediate - Whether to use debounced fast smooth-scroll for typing animation ticks.
   */
  public performAutoScroll(immediate = false): void {
    if (typeof window === 'undefined' || !this.scrollContainer?.nativeElement) {
      return;
    }
    const el = this.scrollContainer.nativeElement;

    // If user has scrolled up away from the bottom (> 120px), do not disrupt their reading
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > 120 && this._isUserScrolling) {
      return;
    }

    if (immediate) {
      if (this._immediateScrollDebounceTimer) {
        clearTimeout(this._immediateScrollDebounceTimer);
      }
      this._immediateScrollDebounceTimer = setTimeout(() => {
        this.smoothScrollToBottom(150);
      }, 35);
      return;
    }

    this.smoothScrollToBottom(250);
  }

  /**
   * Smoothly scrolls the container to its maximum scroll extent using an ease-out cubic curve.
   *
   * @param durationMs - Animation duration in milliseconds.
   */
  private smoothScrollToBottom(durationMs = 200): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;

    if (this._scrollAnimationId !== null) {
      cancelAnimationFrame(this._scrollAnimationId);
      this._scrollAnimationId = null;
    }

    const start = el.scrollTop;
    const target = el.scrollHeight - el.clientHeight;
    const distance = target - start;

    if (distance <= 0) {
      return;
    }

    const startTime = performance.now();

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      // EaseOutCubic: 1 - (1 - progress)^3
      const ease = 1 - Math.pow(1 - progress, 3);
      el.scrollTop = start + distance * ease;

      if (progress < 1) {
        this._scrollAnimationId = requestAnimationFrame(step);
      } else {
        this._scrollAnimationId = null;
      }
    };

    this._scrollAnimationId = requestAnimationFrame(step);
  }

  /**
   * Legacy wrapper ensuring full backward compatibility with any direct calls.
   */
  public scrollToBottom(): void {
    this.performAutoScroll(false);
  }

  /**
   * Dispatches text and attachments compiled by the ChatInput widget to the NgRx Effects engine.
   * @param {object} event - Text string and validated FileAttachment objects.
   */
  public handleUserMessage(event: {
    text: string;
    attachments: FileAttachment[];
  }): void {
    const text = event.text;
    const mentionedDataSourceIds: string[] = [];
    const mentionedApplications: any[] = [];
    let overrideAiFunctionId: string | undefined;

    // Parse Data Source Mentions
    const dbs = this.dataSources() || [];
    for (const ds of dbs) {
      // Check if message contains @DataSourceName
      if (text.includes(`@${ds.name}`)) {
        if (ds.connector === 'file-storage') {
          overrideAiFunctionId = 'rag-query-agent';
          mentionedDataSourceIds.push(ds.id);
        }
      }
    }

    // Parse Application Mentions
    const apps = this.projectApplications() || [];
    for (const app of apps) {
      if (text.includes(`@${app.name}`)) {
        mentionedApplications.push({
          id: app.id,
          name: app.name,
          type: 'Agentic-application',
        });
      }
    }

    // Construct merged data payload
    const finalData = { ...this.additionalData };
    if (mentionedDataSourceIds.length > 0) {
      finalData['dataSourceIds'] = mentionedDataSourceIds;
    }
    if (mentionedApplications.length > 0) {
      finalData['applications'] = mentionedApplications;
    }

    this.store.dispatch(
      AiChatActions.sendMessage({
        text: event.text,
        attachments: event.attachments,
        data: finalData,
        aiFunctionIdOverride: overrideAiFunctionId,
      }),
    );
  }

  /**
   * Dispatches a recorded voice Blob to the NgRx Effects engine.
   * @param {object} event - Finalized audio File wrapper.
   */
  public handleVoiceMessage(event: { file: File }): void {
    this.store.dispatch(AiChatActions.sendVoiceMessage({ file: event.file }));
  }
}
